import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { createQueryClient, env } from '@noor/config';
import { toast } from '@noor/lib';
import { getApiErrorMessage } from '@noor/api';
import { dropStaleMockToken } from '@/entities/session';
import { AppProviders, i18n } from '@/app/providers';
import { createAppRouter } from '@/app/router/router';
import '@fontsource-variable/geist-mono/index.css';
import '@/app/styles/index.css';

/** Start MSW before the app boots so the very first query is intercepted. */
async function enableMocking(): Promise<void> {
  if (!env.VITE_ENABLE_MSW) return;
  const { worker } = await import('@/app/mock');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

async function registerFirebaseMessagingWorker(): Promise<void> {
  if (env.VITE_ENABLE_MSW || !('serviceWorker' in navigator)) return;

  const firebaseConfig = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
  };
  if (
    !firebaseConfig.apiKey ||
    !firebaseConfig.projectId ||
    !firebaseConfig.messagingSenderId ||
    !firebaseConfig.appId
  ) {
    return;
  }

  const params = new URLSearchParams(
    Object.entries(firebaseConfig).filter((entry): entry is [string, string] =>
      Boolean(entry[1]),
    ),
  );
  try {
    await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${params.toString()}`,
      { scope: '/fcm/' },
    );
  } catch {
    // Chat over the admin socket remains available if push worker registration fails.
  }
}

async function bootstrap(): Promise<void> {
  // A mock login token left in storage from an MSW session can never
  // authorize live — drop it before anything reads it, so the guards send
  // the user straight to `/login` instead of replaying it into a `401`.
  dropStaleMockToken();

  await enableMocking();
  await registerFirebaseMessagingWorker();

  const queryClient = createQueryClient({
    // The generic fallback is TRANSLATED, not the literal 'Request failed' this
    // used to pass: this is the catch-all every unsuppressed mutation lands on,
    // so it was by far the most-seen error string in the app and the only one
    // that stayed English in all three languages. `getApiErrorMessage` still
    // prefers the backend code's own translation when it recognizes the code —
    // this is only what shows when it does not.
    onMutationError: (error) =>
      toast.error(getApiErrorMessage(error, i18n.t('errors:unknown'))),
  });
  const router = createAppRouter(queryClient);

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element #root not found');

  createRoot(rootElement).render(
    <StrictMode>
      <AppProviders queryClient={queryClient}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

void bootstrap();
